import { memo, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IWizardStepRendererProps, useWizardStep } from '@agilepoint/wat-react-ux';
import { IAuthenticationSettingsTextData } from 'modules/shared/queries';
import { AuthenticationForm } from 'modules/shared/forms';
import { IAuthenticationFormFields } from 'modules/shared/types';
import { useAuthenticationDefaultValues, useAuthenticationProviders } from 'modules/shared/hooks';
import { IMobileApplicationDetails } from 'modules/create-new-app/queries';
import { WizardNavigationActions } from '../footers';
import { MobileAppHelpLink } from 'common';

interface IAuthenticationProps extends IWizardStepRendererProps {
    authMetadata: IAuthenticationSettingsTextData;
    onSaveAuthDetails: (formData: IAuthenticationFormFields, optionsData: any) => Promise<{
        UpdateMobileAppResult: IMobileApplicationDetails;
    }>
}

export const Authentication = memo((props: IAuthenticationProps) => {
    const { stepId, authMetadata, onSaveAuthDetails } = props;
    const { defaultConfig, ...rest } = authMetadata;
    const { saveData, data } = useWizardStep(stepId);
    const defaultValues = useAuthenticationDefaultValues(rest);

    const form = useForm<IAuthenticationFormFields>({
        mode: 'onChange',
        defaultValues
    });

    const { trigger, getValues, reset } = form;

    const providers = useAuthenticationProviders(defaultConfig.toLocaleLowerCase());

    const [optionsData, setOptionsData] = useState(providers);

    const onMarkAsDefault = useCallback((key: React.Key) => {
        const clonedData = optionsData.slice();
        const modifiedOptionsData = clonedData.map((option) => {
            if (option.key === key) {
                option.value.markedAsDefault = true;
            }
            else option.value.markedAsDefault = false;
            return option;
        });
        setOptionsData(modifiedOptionsData);
    }, [optionsData]);

    const onNext = useCallback(async () => {
        if (!await trigger()) {
            return false;
        }
        const formData = getValues();

        return onSaveAuthDetails(formData, optionsData).then((result) => {
            saveData({ updateMobileAppRsponse: result.UpdateMobileAppResult, formData, optionsData });
            return true;
        }).catch(() => false);
    }, [trigger, getValues, onSaveAuthDetails, optionsData, saveData]);

    useEffect(() => {
        if (data) {
            reset({ ...data.formData, ...data.optionsData });
            setOptionsData(data.optionsData);
        }
    }, [reset, data]);

    return (
        <FormProvider {...form}>
            <MobileAppHelpLink id="authentication" />
            <AuthenticationForm optionsData={optionsData} onMarkAsDefault={onMarkAsDefault} containerHeight="calc(100% - 50px)" />
            <WizardNavigationActions
                stepId={stepId}
                hook={onNext} />
        </FormProvider>
    );
});
